import { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { Button, MenuItem, Typography, Container, Paper } from '@mui/material';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { alertActions } from '../../features/auth/alertSlice';
import style from './DayFinder.module.css';

const DayFinder = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    birthday: '',
    birthTime: '',
    birthPlace: '',
    location: '',
    purpose: '',
    description: '',
    ascendant: '',
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.fullName || !form.email || !form.phoneNumber || !form.purpose) {
      dispatch(alertActions.createAlert({ message: 'Please fill in all required fields.', type: 'warning' }));
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/api/auspicious-time-request`, form);
      dispatch(alertActions.createAlert({ message: data.message, type: 'success' }));
      setForm({
        fullName: '',
        email: '',
        phoneNumber: '',
        birthday: '',
        birthTime: '',
        birthPlace: '',
        location: '',
        purpose: '',
        description: '',
        ascendant: '',
      });
    } catch (error) {
      dispatch(alertActions.createAlert({ message: error.response?.data?.message || 'Something went wrong.', type: 'warning' }));
    } finally {
      setLoading(false);
    }
  };

  const purposeOptions = [
    { value: 'buy_vehicle', label: 'Buying a Vehicle' },
    { value: 'buy_gift', label: 'Buying Expensive Gifts' },
    { value: 'go_school', label: 'Best Time to Go to Exams' },
    { value: 'marriage', label: 'Marriage Ceremony' },
    { value: 'house_warming', label: 'House Warming' },
    { value: 'other', label: 'Other Purpose' },
  ];

  return (
    <div className={style.page}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h4" align="center" sx={{ mb: 1, fontWeight: 'bold', color: '#054A49' }}>
            Auspicious Time Finder
          </Typography>
          <Typography variant="subtitle1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
            ඔබගේ විශේෂ අවස්ථාව සඳහා පුණ්‍ය කාලය, වෙලාව සොයාගන්න අපිට කියලා සාදා ගන්න
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              name="fullName"
              label="Full Name *"
              variant="outlined"
              fullWidth
              value={form.fullName}
              onChange={handleFormChange}
            />

            <TextField
              name="email"
              label="Email Address *"
              type="email"
              variant="outlined"
              fullWidth
              value={form.email}
              onChange={handleFormChange}
            />

            <TextField
              name="phoneNumber"
              label="Phone Number *"
              variant="outlined"
              fullWidth
              value={form.phoneNumber}
              onChange={handleFormChange}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="birthday"
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                fullWidth
                value={form.birthday}
                onChange={handleFormChange}
              />

              <TextField
                name="birthTime"
                label="Birth Time"
                type="time"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                fullWidth
                value={form.birthTime}
                onChange={handleFormChange}
              />
            </Box>

            <TextField
              name="birthPlace"
              label="Place of Birth / උපන් ස්ථානය"
              variant="outlined"
              fullWidth
              value={form.birthPlace}
              onChange={handleFormChange}
              placeholder="e.g. Colombo"
            />

            <TextField
              name="ascendant"
              label="Ascendant (Lagna) / ලග්නය"
              variant="outlined"
              fullWidth
              value={form.ascendant}
              onChange={handleFormChange}
              placeholder="e.g. Leo, Virgo..."
            />

            <TextField
              name="location"
              label="Current Location / දැනට පදිංචි ස්ථානය"
              variant="outlined"
              fullWidth
              value={form.location}
              onChange={handleFormChange}
              placeholder="e.g. Kandy"
            />

            <TextField
              name="purpose"
              label="Purpose *"
              select
              variant="outlined"
              fullWidth
              value={form.purpose}
              onChange={handleFormChange}
            >
              {purposeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {form.purpose === 'other' && (
              <TextField
                name="description"
                label="Tell us what you want *"
                multiline
                rows={4}
                variant="outlined"
                fullWidth
                value={form.description}
                onChange={handleFormChange}
                placeholder="Describe the occasion you want an auspicious time for..."
                required
              />
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 2,
                borderRadius: 999,
                py: 1.5,
                backgroundColor: '#054A49',
                '&:hover': { backgroundColor: '#043d3c' }
              }}
            >
              {loading ? 'Submitting...' : 'Find Auspicious Time'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </div>
  );
}

export default DayFinder;